import { useQuery } from '@tanstack/react-query'
import { type GuestScreen } from '@/entities/booking'
import { botLinksQuery, type BotLink } from '@/entities/channel'
import { problemText } from '@/shared/api'
import { Icon } from '@/shared/ui'
import s from './GuestLinkFooter.module.css'

/** Что ушло гостю — ответ сервера, развёрнутый в строку. */
export type GuestLinkSent = { to: string; coverage: ReadonlyArray<string> }

type GuestLinkFooterProps = {
  /**
   * Условия переотправки от сервера. `null` — прочитать экран гостя не удалось; это НЕ повод
   * прятать кнопку: переотправка живёт в сервисе доставки, и его сбой сюда не относится.
   */
  guestLink: GuestScreen['guestLink'] | null
  sent: GuestLinkSent | null
  isPending: boolean
  errorText: string | null
  onResend: () => void
}

/** Идентификатор причины — по нему кнопка ссылается на своё объяснение. */
const REASON_ID = 'guest-link-reason'

/**
 * Подвал поповера: ссылка на бота (3.5) — своя менеджеру и переотправка гостю.
 *
 * ⚠️ ДВЕ РАЗНЫЕ ССЫЛКИ, и путать их нельзя. Менеджеру — ОБЫЧНАЯ ссылка на бота: открыть,
 * посмотреть, убедиться, что бот жив. Гостю — ПЕРСОНАЛЬНАЯ, и её здесь не показывают никогда:
 * она одноразовая и привязывает к брони тот чат, который её открыл. Покажи мы её менеджеру — первое,
 * что он сделает, это нажмёт «проверить», и панель гостя уедет ему; гость останется ни с чем.
 * Поэтому переотправку делает сервер сам, а в ответе только «куда ушло» и «каких ботов покрывает».
 *
 * Состояние отправки живёт ВЫШЕ этого компонента: поповер перечитывает экран гостя на каждое
 * открытие и на возврат фокуса в окно, показывая при этом скелетон, — то есть подтверждение
 * «ссылка ушла» умирало бы ровно в тот момент, когда менеджер сходил в мессенджер проверить и
 * вернулся. А не увидев подтверждения, он нажмёт ещё раз, и гость получит вторую ссылку вместо
 * первой.
 */
export function GuestLinkFooter({
  guestLink,
  sent,
  isPending,
  errorText,
  onResend,
}: GuestLinkFooterProps) {
  const bots = useQuery(botLinksQuery())
  // Экран гостя не прочитался — условий мы не знаем. Даём попробовать: настоящую проверку делает
  // сама отправка, и если слать нечем, ручка ответит отказом с причиной.
  const canResend = guestLink === null || guestLink.canResend
  const reason = guestLink?.unavailableReason ?? 'Переотправить сейчас нельзя.'

  return (
    <div className={s.root}>
      <div className={s.resendRow}>
        <button
          type="button"
          className={s.resend}
          // `aria-disabled`, а не `disabled`: причина висит рядом текстом, и до неё нужно
          // добираться клавиатурой — настоящий disabled выкинул бы кнопку из порядка обхода. По
          // той же причине им же гасим и время полёта: иначе фокус срывается на body.
          aria-disabled={canResend && !isPending ? undefined : true}
          data-blocked={canResend ? undefined : 'true'}
          aria-describedby={canResend ? undefined : REASON_ID}
          onClick={() => {
            if (!canResend || isPending) return
            onResend()
          }}
        >
          <Icon name="refresh" size={11} strokeWidth={2} />
          {isPending ? 'Отправляем…' : 'Переотправить ссылку'}
        </button>
      </div>

      {/* Область смонтирована ВСЕГДА: регион, которого до изменения не было, многие скринридеры не
          зачитывают — а после нажатия на вид меняется только эта строка. */}
      <div aria-live="polite">
        {sent ? (
          <p className={s.sent}>
            {/* Покрытие называем ПОЛНОСТЬЮ, включая адресата, — так и в спеке: «отправлено в MAX,
                ссылки на Telegram и MAX». Вычитать адресата из покрытия нельзя даже соблазнительно:
                подписи чата и бота приходят из РАЗНЫХ справочников и совпадают лишь случайно. */}
            Свежая ссылка ушла гостю в {sent.to}
            {sent.coverage.length > 0 ? ` · открывается в ${sent.coverage.join(', ')}` : ''}. Самой
            ссылки здесь нет и не будет: кто её откроет, тот и заберёт панель гостя.
          </p>
        ) : null}
      </div>

      {errorText ? (
        <p className={s.note} role="alert">
          {errorText}
        </p>
      ) : null}

      {/* Причина, почему нельзя, — от сервера: клиент не знает ни про ботов, ни про закрытую
          панель. Когда можно — показываем, что произойдёт по нажатию. */}
      <p className={s.note} id={canResend ? undefined : REASON_ID}>
        {canResend ? (guestLink?.note ?? '') : reason}
      </p>

      <p className={s.eyebrow}>Боты компании</p>
      {bots.isPending ? (
        <p className={s.note}>Читаем…</p>
      ) : bots.error ? (
        <p className={s.note} role="alert">
          {problemText(bots.error, 'Не удалось узнать, какие боты подключены.')}
        </p>
      ) : (
        <ul className={s.bots} role="list">
          {(bots.data ?? []).map((bot) => (
            <BotRow key={bot.messenger} bot={bot} />
          ))}
        </ul>
      )}

      {/* Ловушка, о которой макет молчит, а спека предупреждает прямо: открыв своего бота, менеджер
          создаст новый чат, и система его не узнает — запись повиснет в «разборе». Привязать её к
          брони значит связать с бронью чат МЕНЕДЖЕРА. */}
      <p className={s.warning}>
        Открыв бота, вы попадёте к нему новым чатом — он повиснет в «разборе». Такую запись нужно
        закрыть, а не привязывать к брони.
      </p>
    </div>
  )
}

function BotRow({ bot }: { bot: BotLink }) {
  return (
    <li className={s.bot}>
      <span className={s.dot} data-state={bot.state} aria-hidden="true" />
      <span className={s.botText}>
        <span className={s.botName}>{bot.messengerLabel}</span>
        {/* Подпись состояния — с сервера: «не удалось узнать» и «не подключён» это РАЗНЫЕ ответы,
            и сводить их к одному значило бы отправить менеджера переподключать живого бота. */}
        <span className={s.botState}> — {bot.stateLabel}</span>
        {bot.botName ? <span className={s.botHandle}>{bot.botName}</span> : null}
      </span>
      {bot.url ? (
        <a className={s.open} href={bot.url} target="_blank" rel="noreferrer noopener">
          Открыть
        </a>
      ) : null}
    </li>
  )
}
